import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { useFormik } from 'formik'
import _ from 'lodash'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ButtonGrid } from '../../components'
import { locale } from '../../locale'
import { deleteUser, updateUser } from '../../state/Firebase/operations'
import { editUserEnd } from '../../state/SystemCache/actions'
import { getUser } from '../../state/UserById/selectors'
import { State, UpdateUserField, User } from '../../types'
import { substruct, toYmdStr } from '../../utils'
import { decrypt } from '../../utils/encrypt'

// exstract
type Fields = Required<UpdateUserField>

const isMail = (mail) => {
  const mailReg =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

  return mailReg.test(mail)
}

type Props = {
  user: User
  handleCancel: () => void
  handleSubmit: (
    id: string,
    fields: Fields,
    addedMails: string[],
    originMail: string,
  ) => void
  handlePasswordReset: (email: string) => void
  handleDeleteUser: () => void
}

function UserForm(props: Props) {
  const { user } = props
  const fields: Fields = {
    label: user.label,
    memo: user.memo || '',
    company: user.company,
    password: decrypt(user.managedPassword),
    allowIntegration: !!user.allowIntegration,
    allowDownload: !!user.allowDownload,
    allowDialGauge: !!user.allowDialGauge,
    allowMailNotify: !!user.allowMailNotify,
    alertMails: user.alertMails || [],
    allowSurfaceTemp: !!user.allowSurfaceTemp,
    contactStartDate: user.contactStartDate || toYmdStr(new Date()),
    contactEndDate: user.contactEndDate || toYmdStr(new Date()),
  }

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    dirty,
    setFieldValue,
    setFieldTouched,
  } = useFormik<Fields>({
    initialValues: fields,
    validate: (values) => {
      const errors: Partial<Record<keyof Fields, string>> = {}

      if (!values.password) {
        errors.password = '必須です。'
      } else if (values.password.length < 8) {
        errors.password = '8文字以上にしてください。'
      }
      if (values.contactStartDate > values.contactEndDate) {
        errors.contactStartDate = '終了日より前にしてください。'
        errors.contactEndDate = '開始日より後にしてください。'
      }
      const mails = values.alertMails.filter(isMail)

      if (mails.length > 5) {
        errors.alertMails = '5つまでしか設定できません。'
      }
      if (mails.length !== _.uniq(mails).length) {
        errors.alertMails = '重複があります。'
      }

      return errors
    },
    onSubmit: (values: Fields) => {
      const alertMails = values.alertMails.filter((l) => l !== '')
      const newMails = substruct(alertMails, user.alertMails || [])

      props.handleSubmit(
        user.id,
        { ...values, alertMails },
        newMails,
        user.email,
      )
    },
  })
  const submittable = dirty && isValid && !isSubmitting

  const change = (name, e) => {
    e.persist()
    handleChange(e)
    setFieldTouched(name, true, false)
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6">
          {locale.clientEntity}編集: {user.email}
        </Typography>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr max-content',
          }}
        >
          <TextField
            id="password"
            name="password"
            autoComplete="off"
            required
            label="パスワード"
            error={touched.password && Boolean(errors.password)}
            value={values.password}
            helperText={touched.password ? errors.password : ''}
            onChange={change.bind(null, 'password')}
          />
        </div>
        <TextField
          id="company"
          name="company"
          autoComplete="munekata-admin-company"
          error={touched.company && Boolean(errors.company)}
          label={`${locale.client.company}`}
          helperText={touched.company ? errors.company : ''}
          value={values.company}
          onChange={change.bind(null, 'company')}
          fullWidth
        />
        <TextField
          id="label"
          name="label"
          autoComplete="off"
          error={touched.label && Boolean(errors.label)}
          label={`${locale.client.name}(※クライアントも閲覧可能なデータです。)`}
          helperText={touched.label ? errors.label : ''}
          value={values.label}
          onChange={change.bind(null, 'label')}
          fullWidth
        />
        <div style={{ display: 'grid', gridAutoFlow: 'column' }}>
          <TextField
            id="contactStartDate"
            type="date"
            error={!!errors.contactStartDate}
            label={locale.client.contactStartDate}
            helperText={errors.contactStartDate}
            value={values.contactStartDate}
            InputLabelProps={{ shrink: true }}
            onChange={change.bind(null, 'contactStartDate')}
            fullWidth
          />
          <TextField
            id="contactEndDate"
            type="date"
            error={!!errors.contactEndDate}
            label={locale.client.contactEndDate}
            helperText={errors.contactEndDate}
            value={values.contactEndDate}
            InputLabelProps={{ shrink: true }}
            onChange={change.bind(null, 'contactEndDate')}
            fullWidth
          />
        </div>
        <TextField
          id="memo"
          name="memo"
          autoComplete="off"
          multiline
          error={touched.memo && Boolean(errors.memo)}
          label="メモ"
          helperText={touched.memo ? errors.memo : ''}
          value={values.memo}
          onChange={change.bind(null, 'memo')}
          fullWidth
        />
        <Typography>◆MUDR100C</Typography>
        <UserControlRadio
          id="allowIntegration"
          legend={`${locale.client.allowIntegration}許可`}
          value={String(values.allowIntegration)}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
        <UserControlRadio
          id="allowDownload"
          legend={`${locale.client.allowDownload}許可`}
          value={String(values.allowDownload)}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
        <UserControlRadio
          id="allowSurfaceTemp"
          legend={`${locale.client.allowSurfaceTemp}許可`}
          value={String(values.allowSurfaceTemp)}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
        <Typography>◆LPMS100</Typography>
        <UserControlRadio
          id="allowDialGauge"
          legend={`${locale.client.allowDialGauge}許可`}
          value={String(values.allowDialGauge)}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
        <UserControlRadio
          id="allowMailNotify"
          legend={`${locale.client.allowMailNotify}許可`}
          value={String(values.allowMailNotify)}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
        <TextField
          style={{ width: '300px' }}
          id="alertMails"
          rows={5}
          rowsMax={5}
          multiline
          label={`メール送信先追加 ${
            values.alertMails.filter(isMail).length
          }/5 (最大5件まで)`}
          value={String(values.alertMails.join('\n'))}
          error={Boolean(errors.alertMails)}
          helperText={errors.alertMails || ''}
          onChange={(e) =>
            setFieldValue('alertMails', e.currentTarget.value.split('\n'))
          }
        />

        <ButtonGrid>
          <Button onClick={props.handleCancel}>キャンセル</Button>
          <Button
            color="primary"
            disabled={!submittable}
            onClick={(_e) => handleSubmit()}
          >
            変更する
          </Button>
        </ButtonGrid>
        <Button fullWidth color="secondary" onClick={props.handleDeleteUser}>
          <DeleteIcon />
          {locale.clientEntity}の削除
        </Button>
      </form>
    </div>
  )
}

type OProps = {
  userId: string
}

type SProps = {
  user?: User
}

const UserFormContainer = (op: OProps) => {
  const dispatch = useDispatch()
  const sp = useSelector<State, SProps>((state) => ({
    user: getUser(state, op.userId),
  }))
  const dp = {
    handleSubmit: (
      id: string,
      fields: Fields,
      addedMails: string[],
      originMail: string,
    ) => dispatch(updateUser(id, fields, addedMails, originMail)),
    handleCancel: () => dispatch(editUserEnd()),
    handlePasswordReset: (email) => dispatch(editUserEnd(email)),
    handleDeleteUser: () =>
      dispatch(deleteUser(op.userId, (sp.user && sp.user.label) || op.userId)),
  }

  const { user } = sp

  if (!user) return null

  return <UserForm {...sp} user={user} {...dp} />
}

function UserControlRadio({
  id,
  value,
  setFieldValue,
  setFieldTouched,
  legend,
}: {
  id: string
  value: string
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined,
  ) => unknown
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => unknown
  legend: string
}) {
  return (
    <FormControl component="fieldset" fullWidth>
      <FormLabel component="legend">{legend}</FormLabel>
      <RadioGroup
        id={id}
        name={id}
        aria-label={id}
        row
        value={value}
        onChange={(e) => {
          e.persist()

          setFieldValue(id, e.target.value === 'true')
          setFieldTouched(id, true, false)
        }}
      >
        <FormControlLabel value="true" control={<Radio />} label="許可する" />
        <FormControlLabel
          value="false"
          control={<Radio />}
          label="許可しない"
        />
      </RadioGroup>
    </FormControl>
  )
}

export default UserFormContainer
